/**
 * Cover (BUILDLIST 37.18): a unit below a crater rim takes reduced knockback (and, if the dial is set, blast)
 * from shots landing outside it. Pure; reads the heightmap only.
 *
 * The rule: sample the terrain along the great-circle arc from the unit toward the impact; the RIM is the highest
 * sample above the unit's own surface radius. A unit on a crater floor with the impact beyond the lip has a rim of
 * about the crater's depth; an impact inside the same bowl has none (the samples lie below the unit); a unit on a
 * hilltop has none. Cover scales linearly to COVER_FULL_RIM.
 */
import type { PlanetState, Vec3 } from '../types';
import { vecNormalize, vecSub, vecScale, vecAdd, vecDot, vecLength, vecCross } from '../math';
import { getSurfaceRadius } from './surface';
import { dacos, dcos, dsin } from './dmath';
import { COVER_FULL_RIM, COVER_KNOCKBACK_REDUCTION, COVER_DAMAGE_REDUCTION, COVER_SAMPLES } from '../constants';

/** Height of the highest terrain between a unit (surface direction) and an impact point, above the unit's footing. 0 = nothing between. */
export function rimBetween(planet: PlanetState, unitDir: Vec3, impactPoint: Vec3): number {
  const a = vecNormalize(unitDir);
  const b = vecNormalize(vecSub(impactPoint, planet.position));
  const dot = Math.max(-1, Math.min(1, vecDot(a, b)));
  const angle = dacos(dot);
  if (angle < 1e-4 || !Number.isFinite(angle)) return 0;
  // an orthonormal frame in the plane of the arc: a and the component of b perpendicular to a
  let perp = vecSub(b, vecScale(a, dot));
  const pl = vecLength(perp);
  if (pl < 1e-9) { const anyAxis = Math.abs(a.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 }; perp = vecCross(a, anyAxis); }
  perp = vecNormalize(perp);
  const base = getSurfaceRadius(planet, a);
  let rim = 0;
  for (let i = 1; i <= COVER_SAMPLES; i++) {
    const t = (i / (COVER_SAMPLES + 1)) * angle;
    const dir = vecAdd(vecScale(a, dcos(t)), vecScale(perp, dsin(t)));
    rim = Math.max(rim, getSurfaceRadius(planet, dir) - base);
  }
  return rim;
}

/** 0..1 of the rim's cover: 0 = none, 1 = a full rim (COVER_FULL_RIM or higher). */
export function coverAmount(rim: number): number {
  return Math.max(0, Math.min(1, rim / COVER_FULL_RIM));
}

/** Multiplier on knockback strength for a unit with this much rim between it and the impact. */
export function coverKnockbackFactor(rim: number): number {
  return 1 - COVER_KNOCKBACK_REDUCTION * coverAmount(rim);
}

/** Multiplier on blast damage (1 while COVER_DAMAGE_REDUCTION is 0). */
export function coverDamageFactor(rim: number): number {
  return 1 - COVER_DAMAGE_REDUCTION * coverAmount(rim);
}

/**
 * The `count` lowest surface points within `arcUnits` of a direction, as surface directions — the craters a unit
 * could walk into this turn (37.18c "dig in"). Excludes the unit's own vertex. Pure; one pass over the heightmap.
 */
export function lowestSpotsWithin(planet: PlanetState, dir: Vec3, arcUnits: number, count = 3): Vec3[] {
  const a = vecNormalize(dir);
  const bp = planet.basePositions, hm = planet.heightmap;
  const maxAngle = arcUnits / Math.max(1, planet.radius);
  const cosMin = dcos(maxAngle);
  const own = getSurfaceRadius(planet, a);
  const found: Array<{ i: number; r: number }> = [];
  for (let i = 0; i < hm.length; i++) {
    const x = bp[i * 3], y = bp[i * 3 + 1], z = bp[i * 3 + 2];
    const len = Math.sqrt(x * x + y * y + z * z);
    if (len < 1e-9) continue;
    const dot = (x * a.x + y * a.y + z * a.z) / len;
    if (dot < cosMin || dot > 0.99999) continue;
    const r = planet.radius + hm[i];
    if (r >= own - 0.1) continue;           // only spots lower than where it stands
    found.push({ i, r });
  }
  found.sort((p, q) => p.r - q.r);
  const out: Vec3[] = [];
  for (const f of found) {
    const d = vecNormalize({ x: bp[f.i * 3], y: bp[f.i * 3 + 1], z: bp[f.i * 3 + 2] });
    if (out.some(o => vecDot(o, d) > 0.995)) continue;   // one per hole
    out.push(d);
    if (out.length >= count) break;
  }
  return out;
}

/**
 * How much cover a spot offers against artillery from anywhere: the highest terrain around it (within `arc`
 * units along the surface, in `dirs` directions) above its own footing, as a 0..1 amount. The bot's "is this a
 * hole worth standing in" — it does not know where the next shot comes from, so it looks all round.
 */
export function coverAround(planet: PlanetState, dir: Vec3, arcs: readonly number[] = [1.5, 3.0], dirs = 6): number {
  const a = vecNormalize(dir);
  const anyAxis = Math.abs(a.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
  const u = vecNormalize(vecCross(a, anyAxis));
  const v = vecCross(a, u);
  const base = getSurfaceRadius(planet, a);
  let rim = 0;
  for (const arc of arcs) {
    const t = arc / Math.max(1, planet.radius);
    for (let i = 0; i < dirs; i++) {
      const ang = (i / dirs) * Math.PI * 2;
      const tangent = vecAdd(vecScale(u, dcos(ang)), vecScale(v, dsin(ang)));
      const sample = vecAdd(vecScale(a, dcos(t)), vecScale(tangent, dsin(t)));
      rim = Math.max(rim, getSurfaceRadius(planet, sample) - base);
    }
  }
  return coverAmount(rim);
}
